import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CheckIcon, ShieldCheckIcon } from 'lucide-react';
import { Product } from '../../types';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { useTranslation } from '../../lib/i18n';
import { formatEur } from '../../lib/format';
interface ProductCardProps {
  product: Product;
  layout?: 'vertical' | 'horizontal';
  onRequestQuote?: (productId: string) => void;
  /** Optional — focuses this model's column in the comparison table. */
  onSelect?: (productId: string) => void;
}
export function ProductCard({
  product,
  layout = 'vertical',
  onRequestQuote,
  onSelect
}: ProductCardProps) {
  const {
    t,
    lang
  } = useTranslation();
  const soldOut = product.soldOut === true;
  const comingSoon = product.comingSoon === true;
  const disabled = soldOut || comingSoon;
  if (layout === 'horizontal') {
    return <Card hover className={`flex flex-col md:flex-row gap-4 md:gap-6 ${disabled ? 'opacity-70' : ''}`} data-id="element-425" data-mp="gehjt4nazifd">
        <div className="w-full md:w-1/3 flex-shrink-0 relative bg-dark-bg rounded-industrial min-h-[300px] md:min-h-[350px] overflow-hidden" data-id="element-426" data-mp="jaoo5fr6r0mb">
          <img src={product.image} alt={`${product.name} ${product.model}`} className={`w-full h-full object-contain ${disabled ? 'brightness-40 grayscale' : ''}`} data-id="element-427" data-mp="t7b9e5nfbtov" />
          {disabled && <div className="absolute inset-0 flex items-center justify-center" data-id="element-428" data-mp="126ydx0bg9fm5">
              <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-429" data-mp="176jd9buj7pze" />
              <div className={`relative ${soldOut ? 'bg-red-600 text-white border-red-400' : 'bg-gradient-to-r from-brand-yellow via-yellow-200 to-brand-yellow text-dark-bg animate-shine border-yellow-300'} font-black text-base tracking-widest uppercase px-6 py-2 rounded-full shadow-[0_0_30px_rgba(253,185,19,0.5)] whitespace-nowrap border-2`} data-id="element-430" data-mp="1bxt1q94cgn9p">
                {soldOut ? lang === 'en' ? 'Sold out' : lang === 'ru' ? 'Продано' : 'Prodato' : lang === 'en' ? 'Coming soon' : lang === 'ru' ? 'Скоро' : 'Uskoro dostupno'}
              </div>
            </div>}
        </div>
        <div className="flex-1 flex flex-col" data-id="element-431" data-mp="9isdmhlbnvta">
          <div className="mb-3 md:mb-4" data-id="element-432" data-mp="9hh19tdt9fqk">
            <h3 className="text-xl md:text-2xl font-bold text-white mb-1" data-id="element-433" data-mp="4kh304n7dha0">
              {product.name}
            </h3>
            <p className="text-base md:text-lg text-brand-yellow font-semibold" data-id="element-434" data-mp="ik8bxqyv47h5">
              {product.model}
            </p>
          </div>
          <p className="text-sm md:text-base text-gray-300 mb-3 md:mb-4" data-id="element-435" data-mp="diss228f870c">
            {lang === 'en' && product.descriptionEn ? product.descriptionEn : lang === 'ru' && product.descriptionRu ? product.descriptionRu : product.description}
          </p>
          <div className="mb-3 md:mb-4 space-y-2" data-id="element-436" data-mp="uwk9fctdmnf0">
            <div className="flex items-start gap-2 p-2 bg-brand-yellow bg-opacity-10 rounded border border-brand-yellow border-opacity-30" data-id="element-437" data-mp="zs9efvo60wip">
              <ShieldCheckIcon className="w-4 h-4 md:w-5 md:h-5 text-brand-yellow flex-shrink-0 mt-0.5" data-id="element-438" data-mp="m037n06wk1vx" />
              <div data-id="element-439" data-mp="q14xwriud3lr">
                <p className="text-xs md:text-sm font-semibold text-brand-yellow" data-id="element-440" data-mp="c3zxb71wcwxw">
                  {t.showcase.safetyTitle}
                </p>
                <p className="text-xs text-gray-300" data-id="element-441" data-mp="h4e3sknunytt">{t.showcase.safetyText}</p>
              </div>
            </div>
            {(lang === 'en' && product.highlightsEn ? product.highlightsEn : lang === 'ru' && product.highlightsRu ? product.highlightsRu : product.highlights).slice(0, 3).map((highlight, index) => <div key={index} className="flex items-center gap-2" data-id="element-442" data-mp="21gbrcu0ti2ic" data-mpi={index}>
                  <CheckIcon className="w-3 h-3 md:w-4 md:h-4 text-brand-yellow flex-shrink-0" data-id="element-443" data-mp="1cri8jzd39tk9" />
                  <span className="text-xs md:text-sm text-gray-300" data-id="element-444" data-mp="17tgphefnxu8s">
                    {highlight}
                  </span>
                </div>)}
          </div>
          <div className="grid grid-cols-2 gap-2 md:gap-3 mb-3 md:mb-4 p-2 md:p-3 bg-dark-bg rounded-industrial" data-id="element-445" data-mp="xvj5j34u6jlz">
            <div data-id="element-446" data-mp="ttxsnhsay5bs">
              <p className="text-xs text-gray-500" data-id="element-447" data-mp="tuh2eb7stneq">{t.showcase.capacity}</p>
              <p className="text-xs md:text-sm font-semibold text-white" data-id="element-448" data-mp="p1nvphsrs500">
                {product.specs.capacity}
              </p>
            </div>
            <div data-id="element-449" data-mp="f49swkzrreq6">
              <p className="text-xs text-gray-500" data-id="element-450" data-mp="f4rwk2orgv84">{t.showcase.motor}</p>
              <p className="text-xs md:text-sm font-semibold text-white" data-id="element-451" data-mp="f2f6f28unejz">
                {product.specs.motor}
              </p>
            </div>
            <div data-id="element-452" data-mp="64mqpwz7u4w2">
              <p className="text-xs text-gray-500" data-id="element-453" data-mp="643gz3jpymt4">{t.showcase.battery}</p>
              <p className="text-xs md:text-sm font-semibold text-white" data-id="element-454" data-mp="2hpvlbll40rfk">
                {lang === 'en' && product.specs.batteryEn ? product.specs.batteryEn : lang === 'ru' && product.specs.batteryRu ? product.specs.batteryRu : product.specs.battery}
              </p>
            </div>
            <div data-id="element-455" data-mp="1y2xz9i0hi5ac">
              <p className="text-xs text-gray-500" data-id="element-456" data-mp="1y3g1cv9tvdq6">
                {lang === 'en' ? 'Weight' : lang === 'ru' ? 'Вес' : 'Težina'}
              </p>
              <p className="text-xs md:text-sm font-semibold text-white" data-id="element-457" data-mp="232akjybpj4at">
                {product.specs.weight}
              </p>
            </div>
          </div>
          <p className="text-xs text-gray-500 mb-3 md:mb-4 italic" data-id="element-458" data-mp="2cs3smpcn51ab">
            {t.showcase.batteryNote}
          </p>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 md:gap-4 mt-auto" data-id="element-459" data-mp="1fbn9x5aeq0kk">
            <div data-id="element-460" data-mp="1k9ge5ss5gki7">
              <div className="flex items-baseline gap-2" data-id="element-461" data-mp="1catvp0icja6v">
                {product.priceLabel ? <span className="bg-gradient-to-r from-brand-yellow via-yellow-200 to-brand-yellow text-dark-bg animate-shine font-black text-sm tracking-widest uppercase px-4 py-1.5 rounded-full shadow-[0_0_15px_rgba(253,185,19,0.4)] whitespace-nowrap border border-yellow-300" data-id="element-462" data-mp="1898bsv7e9vn6">
                    {lang === 'en' && product.priceLabelEn ? product.priceLabelEn : lang === 'ru' && product.priceLabelRu ? product.priceLabelRu : product.priceLabel}
                  </span> : <>
                    <div className="flex flex-col" data-id="element-463" data-mp="ybc0a2ajoyoj">
                      {product.originalPrice && <span className="text-sm text-gray-500 line-through mb-0.5" data-id="element-464" data-mp="2eossepdgfgbh">
                          €{formatEur(product.originalPrice)}
                        </span>}
                      <div className="flex items-baseline gap-2" data-id="element-465" data-mp="3rncj5hfduc62">
                        <span className={`text-2xl md:text-3xl font-bold ${disabled ? 'text-gray-500' : 'text-brand-yellow'}`} data-id="element-466" data-mp="3rq72t6qknsyf">
                          €{formatEur(product.price)}
                        </span>
                        <span className="text-xs text-gray-400" data-id="element-467" data-mp="3nn231bxvm5dh">
                          {lang === 'en' ? 'excl. VAT' : lang === 'ru' ? 'без НДС' : 'bez PDV'}
                        </span>
                      </div>
                    </div>
                  </>}
              </div>
              {!disabled && !product.priceLabel && <p className="text-xs text-gray-500 mt-0.5" data-id="element-468" data-mp="y4rvy1tx0ejd">
                  {lang === 'en' ? 'incl. VAT' : lang === 'ru' ? 'с НДС' : 'Sa PDV'}
                  : €{formatEur(product.price * 1.2)} ·
                  {lang === 'en' ? ' Availability on request' : lang === 'ru' ? ' Наличие по запросу' : ' Dostupnost na upit'}
                </p>}
            </div>
            {onRequestQuote && <Button variant="primary" size="md" onClick={disabled ? undefined : () => onRequestQuote(product.id)} disabled={disabled} className={`w-full sm:w-auto ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`} data-id="element-469" data-mp="3hxscxtmctj7m">
                {t.showcase.requestQuote}
                {!disabled && <ArrowRightIcon className="w-4 h-4 ml-2 inline" data-id="element-470" data-mp="1wlqwo5hgw8cl" />}
              </Button>}
          </div>
        </div>
      </Card>;
  }
  // Vertical layout
  return <Card hover className={`flex flex-col h-full ${disabled ? 'opacity-70' : ''}`} data-id="element-471" data-mp="8237o2svqf3z">
      <div className="relative w-full h-64 sm:h-80 bg-dark-bg rounded-industrial mb-4 overflow-hidden" data-id="element-472" data-mp="c3oqe4s3hnm3">
        <img src={product.image} alt={`${product.name} ${product.model}`} className={`w-full h-full object-contain ${disabled ? 'brightness-40 grayscale' : ''}`} data-id="element-473" data-mp="h0ydhds0hn6r" />
        {disabled && <div className="absolute inset-0 flex items-center justify-center" data-id="element-474" data-mp="qr9qrqxl58qb">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-475" data-mp="vpbfards6096" />
            <div className={`relative ${soldOut ? 'bg-red-600 text-white border-red-400' : 'bg-gradient-to-r from-brand-yellow via-yellow-200 to-brand-yellow text-dark-bg animate-shine border-yellow-300'} font-black text-lg tracking-widest uppercase px-8 py-2.5 rounded-full shadow-[0_0_30px_rgba(253,185,19,0.5)] whitespace-nowrap border-2`} data-id="element-476" data-mp="zrzncwuo7353">
              {soldOut ? lang === 'en' ? 'Sold out' : lang === 'ru' ? 'Продано' : 'Prodato' : lang === 'en' ? 'Coming soon' : lang === 'ru' ? 'Скоро' : 'Uskoro dostupno'}
            </div>
          </div>}
      </div>
      <div className="mb-4" data-id="element-477" data-mp="qm1837ahxtoz">
        <h3 className="text-xl font-bold text-white mb-1" data-id="element-478" data-mp="qkq9rmztle59">{product.name}</h3>
        <p className="text-base text-brand-yellow font-semibold" data-id="element-479" data-mp="vkcibj4pe67q">
          {product.model}
        </p>
      </div>
      <p className="text-gray-300 mb-4 flex-1" data-id="element-480" data-mp="10cm6atm9cw7d">
        {lang === 'en' && product.descriptionEn ? product.descriptionEn : lang === 'ru' && product.descriptionRu ? product.descriptionRu : product.description}
      </p>
      <div className="mb-4" data-id="element-481" data-mp="1ebc4kcktd1xb">
        <div className="flex items-baseline gap-2" data-id="element-482" data-mp="1eakknz0kwsmv">
          {product.priceLabel ? <span className="bg-gradient-to-r from-brand-yellow via-yellow-200 to-brand-yellow text-dark-bg animate-shine font-black text-sm tracking-widest uppercase px-4 py-1.5 rounded-full shadow-[0_0_15px_rgba(253,185,19,0.4)] whitespace-nowrap border border-yellow-300" data-id="element-483" data-mp="2ytwe3tv9n158">
              {lang === 'en' && product.priceLabelEn ? product.priceLabelEn : lang === 'ru' && product.priceLabelRu ? product.priceLabelRu : product.priceLabel}
            </span> : <>
              <div className="flex flex-col" data-id="element-484" data-mp="3dkdpa4xpd5fb">
                {product.originalPrice && <span className="text-sm text-gray-500 line-through mb-0.5" data-id="element-485" data-mp="38pq1676m2d19">
                    €{formatEur(product.originalPrice)}
                  </span>}
                <div className="flex items-baseline gap-2" data-id="element-486" data-mp="53947o23qi63">
                  <span className={`text-2xl font-bold ${disabled ? 'text-gray-500' : 'text-brand-yellow'}`} data-id="element-487" data-mp="c0ueqwv0use">
                    €{formatEur(product.price)}
                  </span>
                  <span className="text-xs text-gray-400" data-id="element-488" data-mp="1yk52f1tv153t">
                    {lang === 'en' ? 'excl. VAT' : lang === 'ru' ? 'без НДС' : 'bez PDV'}
                  </span>
                </div>
              </div>
            </>}
        </div>
        {!disabled && !product.priceLabel && <p className="text-xs text-gray-500 mt-0.5" data-id="element-489" data-mp="1fuyqpyrhhdri">
            {lang === 'en' ? 'incl. VAT' : lang === 'ru' ? 'с НДС' : 'Sa PDV'}:
            €{formatEur(product.price * 1.2)} ·
            {lang === 'en' ? ' Availability on request' : lang === 'ru' ? ' Наличие по запросу' : ' Dostupnost na upit'}
          </p>}
      </div>
      {onSelect && !disabled && <button type="button" onClick={() => onSelect(product.id)} aria-label={`${lang === 'en' ? 'View specifications for' : lang === 'ru' ? 'Смотреть характеристики' : 'Pogledaj specifikacije za'} ${product.name} ${product.model}`} className="w-full mb-2 text-sm font-semibold text-brand-yellow hover:text-white border border-brand-yellow/30 hover:border-brand-yellow/60 rounded-lg px-4 py-2.5 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-yellow" data-id="element-490" data-mp="317jh4hfs48q0">
          {lang === 'en' ? 'View specifications' : lang === 'ru' ? 'Смотреть характеристики' : 'Pogledaj specifikacije'}
          <ArrowRightIcon className="w-4 h-4 ml-1.5 inline" data-id="element-491" data-mp="6x8exal0smdo" />
        </button>}
      {onRequestQuote && <Button variant="primary" size="md" className={`w-full ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`} onClick={disabled ? undefined : () => onRequestQuote(product.id)} disabled={disabled} data-id="element-492" data-mp="2u2am8hgx59u">
          {t.showcase.requestQuote}
          {!disabled && <ArrowRightIcon className="w-4 h-4 ml-2 inline" data-id="element-493" data-mp="2671j5fe8ex5l" />}
        </Button>}
    </Card>;
}